/**
 * "View as" (6 Oct): what `/auth/me` says about a view in progress.
 *
 * Plain functions with no imports, so the node tests can run them directly
 * (`tests/view-as.test.mjs`).
 */

/** `/auth/me` → `view_as`: who is viewing, as whom, until when. */
export interface ViewAsState {
  admin_email: string;
  email: string;
  name: string;
  /** ISO timestamp; the view ends then at the latest (or with the session). */
  expires_at: string;
  /** Set for a per-tab demo persona (19 Oct): its own banner shows instead. */
  demo_role?: string | null;
}

/** The part of `/auth/me` this cares about. */
export interface ViewAsMe {
  view_as?: ViewAsState | null;
  can_view_as?: boolean | null;
}

/**
 * The view to show the banner for, or null.
 *
 * Null when there is none, and also once it has run out, in the gap before
 * `/auth/me` is read again: the server has already stopped honouring it.
 */
export function activeViewAs(
  me: ViewAsMe | null | undefined,
  now: number = Date.now(),
): ViewAsState | null {
  const view = me?.view_as ?? null;
  if (view === null) {
    return null;
  }
  const ends = Date.parse(view.expires_at);
  if (Number.isNaN(ends) || ends <= now) {
    return null;
  }
  return view;
}

/** Milliseconds until `view` ends, never negative. */
export function msUntilViewAsEnds(view: ViewAsState, now: number = Date.now()): number {
  const ends = Date.parse(view.expires_at);
  return Number.isNaN(ends) ? 0 : Math.max(0, ends - now);
}

/** The View-as cookie's view for the red banner: not a per-tab demo persona (19 Oct). */
export function cookieViewAs(
  me: ViewAsMe | null | undefined,
  now: number = Date.now(),
): ViewAsState | null {
  const view = activeViewAs(me, now);
  return view !== null && !view.demo_role ? view : null;
}

/** Whether to offer "View as this person": an admin on VIEW_AS_EMAILS, not viewing. */
export function mayOfferViewAs(me: ViewAsMe | null | undefined): boolean {
  return me?.can_view_as === true && (me.view_as ?? null) === null;
}
